import type {
  Acceso,
  AccionCuenta,
  EstadoUsuario,
  Institucion,
  Perfil,
  Relacion,
  UsuarioConAccesos,
} from "@/shared/api";
import { coincide } from "@/shared/lib/busqueda";
import type { TonoPildora } from "@/shared/ui";

export type FiltroEstado = "todos" | EstadoUsuario;
export const ESTADOS: readonly EstadoUsuario[] = ["invitado", "activo", "suspendido", "baja"];

export function esFiltro(x: string | null): x is FiltroEstado {
  return x === "todos" || ESTADOS.includes(x as EstadoUsuario);
}

/** Busca en nombre, correo y códigos de empresa; sin acentos ni mayúsculas. */
export function filtrar(
  lista: readonly UsuarioConAccesos[],
  consulta: string,
  filtro: FiltroEstado,
): UsuarioConAccesos[] {
  return lista.filter(
    (x) =>
      (filtro === "todos" || x.usuario.estado === filtro) &&
      coincide([x.usuario.nombre, x.usuario.correo, ...x.accesos.map((a) => a.institucion)], consulta),
  );
}

export function contarPorEstado(lista: readonly UsuarioConAccesos[]): Record<FiltroEstado, number> {
  const c: Record<FiltroEstado, number> = { todos: lista.length, invitado: 0, activo: 0, suspendido: 0, baja: 0 };
  for (const x of lista) c[x.usuario.estado]++;
  return c;
}

export function tonoEstado(estado: EstadoUsuario): TonoPildora {
  switch (estado) {
    case "activo":
      return "bien";
    case "invitado":
      return "transito";
    case "suspendido":
      return "alerta";
    case "baja":
      return "neutro";
  }
}

/** Qué se puede hacer con la cuenta en cada estado; coincide con las reglas de la API. */
export function accionesDisponibles(estado: EstadoUsuario): AccionCuenta[] {
  switch (estado) {
    case "invitado":
      return ["reenviar-invitacion", "suspender", "baja"];
    case "activo":
      return ["cerrar-sesiones", "suspender", "baja"];
    case "suspendido":
      return ["reactivar", "baja"];
    case "baja":
      return [];
  }
}

/** Acciones que conviene explicar con un motivo, para quien revise el historial. */
export function pideMotivo(accion: AccionCuenta): boolean {
  return accion === "suspender" || accion === "baja";
}

/** Una acción que no tiene vuelta atrás se muestra con más peso. */
export function esDefinitiva(accion: AccionCuenta): boolean {
  return accion === "baja";
}

/** Accesos vigentes primero (por empresa); los quitados aparte, como historial. */
export function separarAccesos(accesos: readonly Acceso[]): { vigentes: Acceso[]; quitados: Acceso[] } {
  const orden = (a: Acceso, b: Acceso) => a.institucion.localeCompare(b.institucion);
  return {
    vigentes: accesos.filter((a) => a.estado !== "baja").sort(orden),
    quitados: accesos.filter((a) => a.estado === "baja").sort(orden),
  };
}

/** Instituciones habilitadas que el usuario todavía no ve. */
export function empresasParaAgregar(habilitadas: readonly Institucion[], accesos: readonly Acceso[]): Institucion[] {
  const tiene = new Set(accesos.filter((a) => a.estado !== "baja").map((a) => a.institucion));
  return habilitadas.filter((i) => !tiene.has(i.institucion));
}

export interface DatosInvitacion {
  readonly correo: string;
  readonly nombre: string;
  readonly institucion: string;
  readonly perfil: Perfil;
}

export type ErroresInvitacion = Partial<Record<"correo" | "nombre" | "empresa", true | undefined>>;

const CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function normalizarCorreo(correo: string): string {
  return correo.trim().toLowerCase();
}

/** Mismas reglas que la API (correo con formato, nombre, empresa elegida); el servidor vuelve a validar. */
export function validarInvitacion(d: DatosInvitacion): ErroresInvitacion {
  const e: ErroresInvitacion = {};
  const correo = normalizarCorreo(d.correo);
  if (!CORREO.test(correo) || correo.length > 254) e.correo = true;
  if (!d.nombre.trim()) e.nombre = true;
  if (!d.institucion) e.empresa = true;
  return e;
}

/** Una institución relacionada (no la propia) siempre necesita motivo: así lo exige la API. */
export function faltaMotivo(relacion: Relacion, motivo: string): boolean {
  return relacion !== "propia" && !motivo.trim();
}
